import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import RoleChangeModal from '../components/RoleChangeModal';
import EntitlementsModal from '../components/EntitlementsModal';
import AuditLogModal from '../components/AuditLogModal';
import { useAuth } from '../AuthContext';

const PAGE_SIZE = 25;

function UserManagementPage() {
  // Auth gate
  const { isLoggedIn, roles: authRoles = [] } = useAuth();
  const isAdmin = useMemo(() => {
    const lowers = authRoles.map(r => String(r).toLowerCase());
    return lowers.includes('admin') || lowers.includes('superadmin');
  }, [authRoles]);

  // Data
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [roles, setRoles] = useState([]); // [{ role_id, role_name, ... }]

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // user_id currently being mutated
  const [error, setError] = useState(null);
  const [actionMessage, setActionMessage] = useState('');

  // Pagination + search
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [isEntModalOpen, setIsEntModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [entitlements, setEntitlements] = useState([]);
  const [auditLog, setAuditLog] = useState([]);

  // Debounce search -> query param
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch roles once (when admin)
  useEffect(() => {
    if (!isLoggedIn || !isAdmin) return;
    let cancelled = false;
    api.get('/admin/roles')
      .then(res => { if (!cancelled) setRoles(res.data || []); })
      .catch(() => { if (!cancelled) setRoles([]); });
    return () => { cancelled = true; };
  }, [isLoggedIn, isAdmin]);

  // Fetch users + states when page/search changes (admin only)
  useEffect(() => {
    if (!isLoggedIn || !isAdmin) return;
    setIsLoading(true);
    setError(null);
    setActionMessage('');

    const ac = new AbortController();
    (async () => {
      try {
        const [usersRes, statesRes] = await Promise.all([
          api.get('/admin/users', { params: { page, perPage: PAGE_SIZE, search }, signal: ac.signal }),
          api.get('/admin/states', { signal: ac.signal })
        ]);

        const usersPayload = usersRes.data?.users ?? usersRes.data ?? [];
        const totalPayload =
          usersRes.data?.pagination?.total ??
          usersRes.data?.total ??
          (Array.isArray(usersPayload) ? usersPayload.length : 0) ?? 0;

        setUsers(Array.isArray(usersPayload) ? usersPayload : []);
        setTotalUsers(Number.isFinite(totalPayload) ? totalPayload : 0);
        setStates(statesRes.data || []);
      } catch (err) {
        if (ac.signal.aborted) return;
        const msg = err.response?.data?.message || String(err);
        setError(msg);
        console.error('[UserManagementPage] Data load error:', msg, err);
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [isLoggedIn, isAdmin, page, search]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalUsers || 0) / PAGE_SIZE)),
    [totalUsers]
  );

  // --- Role Modal ---
  const openRoleModal = useCallback((user) => {
    setSelectedUser(user);
    setIsRoleModalOpen(true);
    setActionMessage('');
  }, []);
  const closeRoleModal = useCallback(() => setIsRoleModalOpen(false), []);

  const handleRoleChange = useCallback(async (userId, roleData) => {
  const role_id = Number(roleData?.role_id);
  const role_name = String(roleData?.role_name || '').trim();
  if (!role_id && !role_name) {
    setActionMessage('Error updating role: no role selected.');
    return;
  }

  setRowBusy(userId);
  setActionMessage('');
  try {
    // SEND BOTH: role_id (preferred) AND role (string) for legacy handlers
    const payload = { role_id, role: role_name };

    // Debug locally so you can verify the payload in the console
    // console.debug('[handleRoleChange] PUT payload:', payload);

    await api.put(`/admin/users/${userId}/role`, payload);

    // Optimistic UI
    setUsers(prev =>
      prev.map(u => (u.user_id === userId ? { ...u, roles: [role_name] } : u))
    );
    setActionMessage(`User role updated to ${role_name}.`);
    closeRoleModal();
  } catch (err) {
    const msg = err.response?.data?.message || String(err);
    setActionMessage(`Error updating role: ${msg}`);
    console.error('[UserManagementPage] Role change error:', msg, err);
  } finally {
    setRowBusy(null);
  }
}, [closeRoleModal]);


  // Delete user
  const handleDeleteUser = useCallback(async (userId) => {
    setActionMessage('');
    if (!window.confirm('Delete this user? This action is permanent.')) return;
    setRowBusy(userId);
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.user_id !== userId));
      setActionMessage('User deleted.');
    } catch (err) {
      const msg = err.response?.data?.message || String(err);
      setActionMessage(`Error deleting user: ${msg}`);
      console.error('[UserManagementPage] Delete user error:', msg, err);
    } finally {
      setRowBusy(null);
    }
  }, []);

  // Change username
  const handleChangeUsername = useCallback(async (userId) => {
    setActionMessage('');
    const newUsername = prompt('Enter a new username:');
    if (!newUsername) return;
    setRowBusy(userId);
    try {
      const username = newUsername.trim();
      await api.put(`/admin/users/${userId}/username`, { username });
      setUsers(prev => prev.map(u => (u.user_id === userId ? { ...u, username } : u)));
      setActionMessage('Username updated.');
    } catch (err) {
      const msg = err.response?.data?.message || String(err);
      setActionMessage(`Error updating username: ${msg}`);
      console.error('[UserManagementPage] Change username error:', msg, err);
    } finally {
      setRowBusy(null);
    }
  }, []);

  // Entitlements
  const openEntitlementModal = useCallback(async (user) => {
    setSelectedUser(user);
    setIsEntModalOpen(true);
    setActionMessage('');
    try {
      const res = await api.get(`/admin/users/${user.user_id}/entitlements`);
      setEntitlements(res.data || []);
    } catch (err) {
      setEntitlements([]);
      setActionMessage('Could not load entitlements.');
      console.error('[UserManagementPage] Entitlements error:', err);
    }
  }, []);

  const grantEntitlement = useCallback(async (stateId) => {
    if (!selectedUser) return;
    if (stateId === 1) { setActionMessage('State 1 cannot be granted.'); return; }
    setActionMessage('');
    setRowBusy(selectedUser.user_id);
    try {
      await api.post(`/admin/users/${selectedUser.user_id}/entitlements`, { stateId });
      const res = await api.get(`/admin/users/${selectedUser.user_id}/entitlements`);
      setEntitlements(res.data || []);
      setActionMessage('Entitlement granted.');
    } catch (err) {
      const msg = err.response?.data?.message || String(err);
      setActionMessage(`Error granting entitlement: ${msg}`);
    } finally {
      setRowBusy(null);
    }
  }, [selectedUser]);

  const revokeEntitlement = useCallback(async (entitlementId) => {
    if (!selectedUser) return;
    setActionMessage('');
    setRowBusy(selectedUser.user_id);
    try {
      await api.delete(`/admin/users/${selectedUser.user_id}/entitlements/${entitlementId}`);
      const res = await api.get(`/admin/users/${selectedUser.user_id}/entitlements`);
      setEntitlements(res.data || []);
      setActionMessage('Entitlement revoked.');
    } catch (err) {
      const msg = err.response?.data?.message || String(err);
      setActionMessage(`Error revoking entitlement: ${msg}`);
    } finally {
      setRowBusy(null);
    }
  }, [selectedUser]);

  // Audit log
  const openAuditModal = useCallback(async (user) => {
    setSelectedUser(user);
    setIsAuditModalOpen(true);
    setLoadingAudit(true);
    setActionMessage('');
    try {
      const res = await api.get(`/admin/users/${user.user_id}/audit-log`);
      setAuditLog(res.data || []);
    } catch (err) {
      setAuditLog([]);
      setActionMessage('Could not load audit log.');
      console.error('[UserManagementPage] Audit log error:', err);
    } finally {
      setLoadingAudit(false);
    }
  }, []);

  // Render gates
  if (!isLoggedIn) {
    return <div className="admin-container"><h1>User Management</h1><p>Please sign in.</p></div>;
  }
  if (!isAdmin) {
    return <div className="admin-container"><h1>User Management</h1><p>Admin access required.</p></div>;
  }

  return (
    <>
      {isRoleModalOpen && selectedUser && (
        <RoleChangeModal
          user={selectedUser}
          roles={roles} // expects [{role_id, role_name}, ...]
          // RoleChangeModal must call onSave({ role_id, role_name })
          onSave={(roleData) => handleRoleChange(selectedUser.user_id, roleData)}
          onClose={() => setIsRoleModalOpen(false)}
        />
      )}

      {isEntModalOpen && selectedUser && (
        <EntitlementsModal
          user={selectedUser}
          entitlements={entitlements}
          states={states}
          onGrant={grantEntitlement}
          onRevoke={revokeEntitlement}
          onClose={() => setIsEntModalOpen(false)}
        />
      )}

      {isAuditModalOpen && selectedUser && (
        <AuditLogModal
          user={selectedUser}
          auditLog={auditLog}
          isLoading={loadingAudit}
          onClose={() => setIsAuditModalOpen(false)}
        />
      )}

      <div className="admin-container">
        <h1>User Management</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Search by username or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 260, padding: 6, fontSize: 14 }}
          />
          <button
            className="btn"
            onClick={() => { setSearchInput(''); setPage(1); }}
            disabled={isLoading}
          >
            Clear
          </button>
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#666' }}>
            {totalUsers} total
          </div>
        </div>

        {actionMessage && (
          <div
            role="alert"
            style={{
              background: actionMessage.toLowerCase().includes('error') ? '#fde2e2' : '#eef2ff',
              border: `1px solid ${actionMessage.toLowerCase().includes('error') ? '#f5a3a3' : '#c7d2fe'}`,
              padding: '8px 10px',
              borderRadius: 6,
              marginBottom: 10,
              color: '#111',
              fontSize: 13,
            }}
          >
            {actionMessage}
          </div>
        )}

        {error && (
          <div
            role="alert"
            style={{
              background: '#fde2e2',
              border: '1px solid #f5a3a3',
              padding: '8px 10px',
              borderRadius: 6,
              marginBottom: 10,
              color: '#8a0f0f',
              fontSize: 13,
            }}
          >
            Error: {error}
          </div>
        )}

        {isLoading ? (
          <p>Loading users…</p>
        ) : (
          <>
            <table className="user-management-table" style={{ width: '100%', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>User ID</th>
                  <th>Email</th>
                  <th>Username</th>
                  <th style={{ width: 200 }}>Roles</th>
                  <th style={{ width: 260 }}>Permissions</th>
                  <th style={{ width: 380 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(!users || users.length === 0) ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 18 }}>No users found.</td></tr>
                ) : users.map(user => {
                  const rolesText = (user.roles && user.roles.length) ? user.roles.join(', ') : '—';
                  const permsText = (user.permissions && user.permissions.length) ? user.permissions.join(', ') : '—';
                  const busy = rowBusy === user.user_id;
                  return (
                    <tr key={user.user_id}>
                      <td>{user.user_id}</td>
                      <td title={user.email}>{user.email}</td>
                      <td title={user.username || 'N/A'}>{user.username || 'N/A'}</td>
                      <td title={rolesText}>
                        <div style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 190,
                          border: '1px solid #eee',
                          borderRadius: 6,
                          padding: '4px 6px',
                          background: '#fafafa'
                        }}>{rolesText}</div>
                      </td>
                      <td title={permsText}>
                        <div style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 250,
                          border: '1px solid #eee',
                          borderRadius: 6,
                          padding: '4px 6px',
                          background: '#fafafa'
                        }}>{permsText}</div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <button className="btn" disabled={busy} onClick={() => handleChangeUsername(user.user_id)}>
                            Username
                          </button>
                          <button className="btn" disabled={busy} onClick={() => openEntitlementModal(user)}>
                            Entitlements
                          </button>
                          <button className="btn" disabled={busy} onClick={() => openAuditModal(user)}>
                            Audit Log
                          </button>
                          <button className="btn" disabled={busy} onClick={() => handleDeleteUser(user.user_id)}>
                            Delete
                          </button>
                          <button className="btn role-change-btn" disabled={busy} onClick={() => openRoleModal(user)}>
                            Change Role
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                className="btn"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 13 }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next ›
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default UserManagementPage;
